/**
 * Web Push: registro de Service Worker, permission, y suscripción al backend.
 *
 * Flow:
 *   1. registerServiceWorker() en startup
 *   2. (opcional, gating) requestPushPermissionAndSubscribe() cuando el user clickea "activar notificaciones"
 *   3. Backend recibe la subscription y guarda en DB
 *   4. Cuando llega un evento (msg, trade), backend llama send_web_push_to_user
 */
import { api } from "./client";

interface PublicKeyResp {
  public_key: string | null;
  enabled: boolean;
}

const SW_PATH = "/sw.js";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) arr[i] = rawData.charCodeAt(i);
  return arr;
}

export function pushSupported(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    return await navigator.serviceWorker.register(SW_PATH);
  } catch (e) {
    console.warn("[push] sw register failed", e);
    return null;
  }
}

export async function getPushPermission(): Promise<NotificationPermission> {
  if (typeof Notification === "undefined") return "denied";
  return Notification.permission;
}

export async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const r = await api<PublicKeyResp>("/push/public-key");
    return r.enabled ? r.public_key : null;
  } catch {
    return null;
  }
}

/** Pide permiso, suscribe el browser y envía la subscription al backend.
 *  Retorna true si quedó suscrito al final del flujo. */
export async function requestPushPermissionAndSubscribe(): Promise<boolean> {
  if (!pushSupported()) return false;

  const reg = await registerServiceWorker();
  if (!reg) return false;

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return false;

  const publicKey = await fetchVapidPublicKey();
  if (!publicKey) {
    console.warn("[push] backend no tiene VAPID keys configuradas");
    return false;
  }

  // Si ya hay una subscription previa, reusar
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    // Cast needed: TS lib emite ArrayBufferLike pero PushManager acepta Uint8Array
    const appKey = urlBase64ToUint8Array(publicKey) as unknown as BufferSource;
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appKey,
    });
  }

  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    return false;
  }

  await api("/push/subscribe", {
    method: "POST",
    body: JSON.stringify({
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    }),
  });

  return true;
}

export async function unsubscribePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await api("/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint: sub.endpoint }) });
  } catch {
    /* ignore */
  }
  await sub.unsubscribe();
}
